import React, { useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Button, HStack, Text, Alert, AlertIcon, Spinner, useToast,
} from "@chakra-ui/react";
import {
  runRlsScan, fetchPhiDeidStatus, testPhiDeidPath, setMasterNoteDeid,
} from "../../services/rlsAudit";
import { fetchRdsHealth } from "../../services/rdsHealth";
import RlsReport from "./RlsReport";
import RlsFixDialog from "./RlsFixDialog";
import PhiDeidPanel from "./PhiDeidPanel";
import RdsMigrationLayouts from "./rdsMigration/RdsMigrationLayouts";
import SyncParityPanel from "./SyncParityPanel";
import AuditLogsPanel from "./AuditLogsPanel";
import UserMgmtPanel from "./UserMgmtPanel";
import {
  readCache, writeCache, formatAge, RLS_SCAN_KEY,
} from "../../utils/resultCache";

export default function HipaaComplianceModal({ isOpen, onClose }) {
  const toast = useToast();
  // Restored from the last run so closing the modal — or the tab — does not
  // throw away a scan that took real time to produce. Run Scan always refetches.
  const cached = readCache(RLS_SCAN_KEY);
  const [report, setReport] = useState(cached?.data ?? null);
  const [scannedAt, setScannedAt] = useState(cached?.savedAt ?? null);
  const [scanning, setScanning] = useState(false);
  const [fixTarget, setFixTarget] = useState(null);

  const onScan = async () => {
    setScanning(true);
    try {
      const fresh = await runRlsScan();
      setReport(fresh);
      setScannedAt(new Date().toISOString());
      writeCache(RLS_SCAN_KEY, fresh);
    } catch (err) {
      toast({
        title: "Scan failed",
        description: err.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setScanning(false);
    }
  };

  const onFix = (schema, table) => setFixTarget({ schema, table });
  const onFixAll = (schema) => setFixTarget({ batch: true, schema });

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>HIPAA Compliance</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs colorScheme="blue" isLazy>
            <TabList>
              <Tab>RLS</Tab>
              <Tab>AI PHI De-identification</Tab>
              <Tab>RDS Migration</Tab>
              <Tab>Logs Review/Auditing</Tab>
              <Tab>User Management</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <HStack mb={4}>
                  <Button colorScheme="blue" onClick={onScan} isLoading={scanning}
                          loadingText="Scanning…">
                    {report ? "Re-run Scan" : "Run Scan"}
                  </Button>
                  {scanning && <Spinner size="sm" />}
                  {report && !scanning && scannedAt && (
                    <Text fontSize="sm" color="gray.600" data-testid="rls-scanned-at">
                      Showing results from {formatAge(scannedAt)} — re-run for current data
                    </Text>
                  )}
                </HStack>
                {!report && !scanning && (
                  <Alert status="info">
                    <AlertIcon />
                    No scan yet — run one to grade RLS coverage across every schema.
                  </Alert>
                )}
                {report && <RlsReport report={report} onFix={onFix} onFixAll={onFixAll} />}
              </TabPanel>

              <TabPanel px={0}>
                <PhiDeidPanel
                  load={fetchPhiDeidStatus}
                  runTest={testPhiDeidPath}
                  setMasterNoteDeid={setMasterNoteDeid}
                />
              </TabPanel>

              <TabPanel px={0}>
                {/* Two views of the same migration: the Command Center says
                    WHICH system serves a screen, Sync Parity says whether the
                    copy in RDS still agrees with the copy in Supabase. The
                    parity tab is lazy on purpose -- the comparison reads every
                    mirrored table on both sides, so it only runs when an
                    operator actually opens it. */}
                <Tabs size="sm" variant="soft-rounded" colorScheme="blue" isLazy>
                  <TabList mb={3}>
                    <Tab>Command Center</Tab>
                    <Tab>Sync Parity</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <RdsMigrationLayouts load={fetchRdsHealth} />
                    </TabPanel>
                    <TabPanel px={0}>
                      <SyncParityPanel />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </TabPanel>

              <TabPanel px={0}>
                <AuditLogsPanel />
              </TabPanel>

              <TabPanel px={0}>
                <UserMgmtPanel />
              </TabPanel>
            </TabPanels>
          </Tabs>

        </ModalBody>
      </ModalContent>
    </Modal>

    {/* Sibling, NOT inside ModalBody. A Chakra Modal rendered inside another
        Modal's body portals out of it and the two focus-locks fight: opening
        the fix dialog blanked both in the browser (jsdom did not reproduce it).
        Keeping them as siblings stacks them correctly. */}
    <RlsFixDialog
      target={fixTarget}
      onClose={() => setFixTarget(null)}
      onApplied={onScan}
    />
    </>
  );
}
